import { useEffect, useMemo, useState } from 'react';
import { subDays } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CountryBar } from 'src/components/country-bar/CountryBar';
import { CountryItem } from 'src/components/country-bar/types';
import { deriveCountryCode } from 'src/components/visibility/hooks/countryCode';
import { useRouter } from 'src/routes/hooks';
import { buildAdsDeepLink } from 'src/components/data-grid/brand-infringement/deepLink';
import { useCountriesCoverageQuery, useCountriesQuery } from 'src/context/api/filters/api';
import { dataApi } from 'src/context/api/data/api';
import getSerpErrorMessage from 'src/components/visibility/components/getSerpErrorMessage';
import {
    InfringementRow,
    useExportTrademarkReportMutation,
    useGetInfringementGridQuery,
    useLazyGetInfringementExportQuery,
    useStartCaptureMutation,
} from 'src/context/api/brandInfringement/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fDate } from 'src/utils/format-time';
import DataGridBrandInfringement from 'src/components/data-grid/brand-infringement/data-grid-brand-infringement';
import { buildCsv, csvFilename, downloadTextFile } from 'src/components/data-grid/brand-infringement/exportUtils';

type Days = 3 | 7 | 30;

const DAYS_OPTIONS: { value: Days; label: string }[] = [
    { value: 3, label: 'Last 3 days' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
];

// The country bar is workspace-level context, so it is deliberately NOT tied to the grid's period
// selector: re-querying coverage on every 3/7/30 switch blanks and reloads the bar for nothing. We
// pin it to the widest period on offer, which makes its list a superset of any selection and keeps
// one cache entry per workspace.
const COVERAGE_LOOKBACK_DAYS = Math.max(...DAYS_OPTIONS.map((option) => option.value));

export default function BrandInfringementView() {
    const router = useRouter();
    const workspace: any = useCurrentWorkspace();
    const [days, setDays] = useState<Days>(7);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [busyDomain, setBusyDomain] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Not derived from `days` on purpose — see COVERAGE_LOOKBACK_DAYS.
    const { startDate, endDate } = useMemo(() => {
        const today = new Date();
        return {
            startDate: fDate(subDays(today, COVERAGE_LOOKBACK_DAYS), 'yyyy-MM-dd'),
            endDate: fDate(today, 'yyyy-MM-dd'),
        };
    }, []);

    // Tells us whether this workspace has more than one country *before* the coverage query lands,
    // so a mono-country workspace never flashes the bar's skeleton. The header already runs this
    // exact query for every workspace (header.tsx), so with identical arguments this is a cache hit
    // rather than a second request. Preferred over `filters.loadedCountries`, which the header only
    // ever writes when non-empty and never resets — it keeps reporting the previous workspace's
    // count after a switch, whereas the cache entry is simply `undefined` while the new one loads.
    const filterStore: any = useSelector((state: any) => state.filters);
    const { data: workspaceCountries } = useCountriesQuery(
        {
            workspace: workspace?.bqTable,
            platforms: filterStore?.platforms,
            startDate: filterStore?.start,
            endDate: filterStore?.end,
        },
        { skip: !workspace?.bqTable }
    );
    const expectsCountryBar = (workspaceCountries?.countries?.length ?? 0) > 1;

    const { data: coverageData, isFetching: isLoadingCountries } = useCountriesCoverageQuery(
        {
            workspace: workspace?.bqTable,
            platforms: undefined,
            devices: undefined,
            startDate,
            endDate,
        },
        { skip: !workspace?.bqTable }
    );

    const countriesData: CountryItem[] = useMemo(() => {
        const rows: any[] = Array.isArray(coverageData?.countries) ? coverageData.countries : [];
        return rows
            .map((row: any) => ({
                country_name: row.region_name,
                country_code: deriveCountryCode(row.region_name),
                share_of_brand_searches:
                    typeof row.brand_traffic_share === 'number' ? row.brand_traffic_share : null,
            }))
            .sort((a, b) => (b.share_of_brand_searches ?? 0) - (a.share_of_brand_searches ?? 0));
    }, [coverageData]);

    useEffect(() => {
        if (selectedCountry === null && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }
    }, [selectedCountry, countriesData]);

    const { data: rawData, isFetching, isError } = useGetInfringementGridQuery(
        { workspaceTable: workspace?.bqTable, country: selectedCountry, days },
        { skip: !workspace?.bqTable || !selectedCountry }
    );

    // Keep the last loaded response around: a filter change (country/days) produces a NEW
    // query, so RTK Query's `data` is `undefined` the time the fetch is in flight. Without
    // retention the grid would collapse to zero rows on every refetch instead of holding its
    // size under the loading overlay.
    const [lastData, setLastData] = useState<typeof rawData>(undefined);
    useEffect(() => {
        if (rawData) setLastData(rawData);
    }, [rawData]);
    const data = rawData ?? lastData;

    const [startCapture] = useStartCaptureMutation();
    const [getExport] = useLazyGetInfringementExportQuery();
    const dispatch = useDispatch();
    const [exportTrademarkReport] = useExportTrademarkReportMutation();

    const handleStartCapture = async (row: InfringementRow) => {
        if (!workspace?.bqTable) return;
        setBusyDomain(row.domain_name);
        try {
            await startCapture({
                workspaceTable: workspace.bqTable,
                domain: row.domain_name,
                searchTerms: row.search_terms ?? [],
                country: selectedCountry,
                countryCode: selectedCountry ? deriveCountryCode(selectedCountry) : null,
            }).unwrap();
        } finally {
            setBusyDomain(null);
        }
    };

    const handleExportCsv = async (domain?: string) => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { rows: exportRows } = await getExport({
                workspaceTable: workspace?.bqTable,
                country: selectedCountry,
                days,
                domain,
            }).unwrap();
            downloadTextFile(csvFilename(workspace?.name ?? '', domain), buildCsv(exportRows), 'text/csv');
        } catch (err) {
            enqueueSnackbar(getSerpErrorMessage(err), { variant: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    // Per-domain export goes through a server-side job: the file is announced by a
    // notification and downloaded from there, so nothing is downloaded here.
    // "Export all" (handleExportCsv) deliberately keeps the client-side path.
    const handleDownloadDomainCsv = async (row: InfringementRow) => {
        if (!workspace?.bqTable || busyDomain) return;
        setBusyDomain(row.domain_name);
        try {
            await exportTrademarkReport({
                workspaceTable: workspace?.bqTable,
                country: selectedCountry,
                days,
                domain: row.domain_name,
            }).unwrap();
            dispatch(dataApi.util.invalidateTags(['Notifications']));
            enqueueSnackbar('Export ready in your notifications', { variant: 'success' });
        } catch (err) {
            // This page owns the feedback for its own export (see
            // SELF_REPORTED_ACTIONS in exports.helpers): the backend message when
            // there is one, an English default otherwise — an unhandled backend
            // exception carries no `data.error`.
            enqueueSnackbar(getSerpErrorMessage(err, 'Export failed. Please try again.'), {
                variant: 'error',
            });
        } finally {
            setBusyDomain(null);
        }
    };

    const handleTermClick = (term: string, row?: InfringementRow) => {
        router.push(buildAdsDeepLink({
            countryCode: selectedCountry ? deriveCountryCode(selectedCountry) : undefined,
            domain: row?.domain_name ?? '',
            searchTerm: term,
            brandInfringement: true,
        }));
    };

    const rows = data?.rows ?? [];

    // Any load, first or not: the whole card stands in as one skeleton block instead of a spinner
    // over the grid, like the Brand Pulse leaderboard and the Explore table. `!data` covers the
    // window before the first response (and while the query is still skipped, waiting on a country).
    const showTableSkeleton = isFetching || (!data && !isError);

    return (
        <Box>
            {/* The skeleton is reserved only when we already know the workspace has several
                countries. On a mono-country workspace it never shows, so the bar no longer flashes
                in and out. */}
            <CountryBar
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                loading={expectsCountryBar && isLoadingCountries}
                selectedColor="#9982cc"
                renderCountryLabel={(c) => (
                    <>
                        {c.country_name}
                        {typeof c.share_of_brand_searches === 'number'
                            ? ` (${Math.round(c.share_of_brand_searches)}%)`
                            : ''}
                    </>
                )}
            />

            <Box
                px="24px"
                // Without a country bar, the `Section` wrapper's top padding (pt: 144px, sized for
                // header + country bar) leaves ~20px too much: pull the content up by that amount.
                // Reserve the gap only when a bar is either already there or known to be coming, so a
                // mono-country workspace settles without shifting the content at all.
                mt={countriesData.length > 1 || (expectsCountryBar && isLoadingCountries) ? '24px' : '-20px'}
            >
                    {/* The card frame and its loading skeleton both live inside the grid component,
                        so swapping one for the other never unmounts it (and never resets its
                        columns, sort or pagination). */}
                    <DataGridBrandInfringement
                        showSkeleton={showTableSkeleton}
                        rows={rows}
                        loading={isFetching}
                        onTermClick={handleTermClick}
                        onStartCapture={handleStartCapture}
                        onDownloadDomainCsv={handleDownloadDomainCsv}
                        onExportAll={() => handleExportCsv()}
                        rightSlot={
                            <Select
                                size="small"
                                value={days}
                                onChange={(event: SelectChangeEvent<number>) =>
                                    setDays(Number(event.target.value) as Days)
                                }
                                sx={{
                                    minWidth: 160,
                                    height: 32,
                                    '& .MuiSelect-select': {
                                        fontSize: '0.8125rem',
                                        // MUI's .MuiInputBase-input height rule wins at equal
                                        // specificity, so force the inner chip to 20px — the 32px
                                        // control less its 1px border and 5px of breathing room.
                                        minHeight: '20px !important',
                                        height: '20px',
                                        boxSizing: 'border-box',
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                {DAYS_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                        busyDomain={busyDomain}
                    />
            </Box>
        </Box>
    );
}
